/**
 * The storage classes the app knows, per provider — a copy of the store
 * crate's catalogue (`engine/crates/bigbear-store/src/storage_class.rs`),
 * which restates `docs/storage-classes.md`. The desktop's setup form, the
 * portal and the Delete provider note read the display names and the same two
 * facts the engine keys off: the retention floor and whether a read waits on a
 * retrieval request. Recorded in ADR 0007.
 *
 * The Rust is the source of truth. `scripts/check-engine-constants.mjs`
 * compares the two row by row, field by field, and fails CI when they
 * disagree, so a row changed here alone does not land. Keep each row on the
 * shape below: the check reads this file as text.
 *
 * Every name is the provider's own, verbatim. Where a floor depends on the
 * account or billing plan (Azure Cool and Cold, Wasabi) the row carries the
 * longest, because the vault does not record its plan.
 */

import type { RetrievalOption } from "./hyperscalerPricing";

/** Every provider the catalogue has rows for, as a vault records it. */
export const STORAGE_PROVIDERS = ["aws", "gcs", "azure", "backblaze", "wasabi"] as const;

export type StorageProvider = (typeof STORAGE_PROVIDERS)[number];

/**
 * One retrieval option a class offers, and how long it typically takes, in
 * the words a person reads — the same text as that option's `typicalWait` in
 * `hyperscalerPricing.ts`, which `storageClasses.test.ts` holds equal.
 */
export interface RetrievalOffer {
  option: RetrievalOption;
  typicalWait: string;
}

/** One class of one provider. */
export interface StorageClass {
  provider: StorageProvider;
  /** The provider's own name for the class, verbatim. */
  displayName: string;
  /** The value sent on write, spelled as the provider spells it; `null` where the provider has one class and takes none. */
  requestValue: string | null;
  /** The retention floor in days; `null` where nothing is billed past a delete. */
  retentionFloorDays: number | null;
  /** Whether a read waits on a retrieval request. */
  retrieval: "immediate" | "delayed";
  /**
   * The retrieval options the app offers on this class, the default first.
   * Empty on every class whose reads do not wait; a class whose reads wait
   * and that lists none is one the app cannot ask for a retrieval, which
   * `canCreateVaultOn` therefore refuses.
   */
  retrievalOptions: readonly RetrievalOffer[];
  /**
   * The document marks this row's floor a gap: not yet read first-hand from a
   * current provider page. Used exactly as a confirmed row is; the mark exists
   * so the next research pass can list what to confirm.
   */
  provisional: boolean;
}

/** The class an unrecognised S3-compatible provider gets. Its floor is unknown. */
export const UNRECOGNISED_PROVIDER_CLASS = "STANDARD";

/** Every class, in `docs/storage-classes.md`'s order. */
export const STORAGE_CLASSES: readonly StorageClass[] = [
  // -- Amazon S3 ------------------------------------------------------------
  { provider: "aws", displayName: "S3 Standard", requestValue: "STANDARD", retentionFloorDays: null, retrieval: "immediate", retrievalOptions: [], provisional: false },
  { provider: "aws", displayName: "Standard-Infrequent Access", requestValue: "STANDARD_IA", retentionFloorDays: 30, retrieval: "immediate", retrievalOptions: [], provisional: false },
  { provider: "aws", displayName: "S3 One Zone-Infrequent Access", requestValue: "ONEZONE_IA", retentionFloorDays: 30, retrieval: "immediate", retrievalOptions: [], provisional: false },
  { provider: "aws", displayName: "Glacier Instant Retrieval", requestValue: "GLACIER_IR", retentionFloorDays: 90, retrieval: "immediate", retrievalOptions: [], provisional: false },
  { provider: "aws", displayName: "S3 Glacier Flexible Retrieval", requestValue: "GLACIER", retentionFloorDays: 90, retrieval: "delayed", retrievalOptions: [{ option: "standard", typicalWait: "3–5 hours" }, { option: "bulk", typicalWait: "5–12 hours" }], provisional: false },
  { provider: "aws", displayName: "S3 Glacier Deep Archive", requestValue: "DEEP_ARCHIVE", retentionFloorDays: 180, retrieval: "delayed", retrievalOptions: [{ option: "standard", typicalWait: "Up to 12 hours" }, { option: "bulk", typicalWait: "Up to 48 hours" }], provisional: false },
  // -- Google Cloud Storage -------------------------------------------------
  { provider: "gcs", displayName: "Standard storage", requestValue: "STANDARD", retentionFloorDays: null, retrieval: "immediate", retrievalOptions: [], provisional: false },
  { provider: "gcs", displayName: "Nearline storage", requestValue: "NEARLINE", retentionFloorDays: 30, retrieval: "immediate", retrievalOptions: [], provisional: false },
  { provider: "gcs", displayName: "Coldline storage", requestValue: "COLDLINE", retentionFloorDays: 90, retrieval: "immediate", retrievalOptions: [], provisional: false },
  { provider: "gcs", displayName: "Archive storage", requestValue: "ARCHIVE", retentionFloorDays: 365, retrieval: "immediate", retrievalOptions: [], provisional: false },
  // -- Azure Blob Storage ----------------------------------------------------
  { provider: "azure", displayName: "Hot tier", requestValue: "Hot", retentionFloorDays: null, retrieval: "immediate", retrievalOptions: [], provisional: false },
  { provider: "azure", displayName: "Cool tier", requestValue: "Cool", retentionFloorDays: 30, retrieval: "immediate", retrievalOptions: [], provisional: false },
  { provider: "azure", displayName: "Cold tier", requestValue: "Cold", retentionFloorDays: 90, retrieval: "immediate", retrievalOptions: [], provisional: false },
  { provider: "azure", displayName: "Archive tier", requestValue: "Archive", retentionFloorDays: 180, retrieval: "delayed", retrievalOptions: [{ option: "standard", typicalWait: "Up to 15 hours" }], provisional: false },
  // -- Backblaze B2 ---------------------------------------------------------
  { provider: "backblaze", displayName: "B2 Cloud Storage", requestValue: null, retentionFloorDays: null, retrieval: "immediate", retrievalOptions: [], provisional: false },
  // -- Wasabi ---------------------------------------------------------------
  { provider: "wasabi", displayName: "Wasabi Hot Cloud Storage", requestValue: "STANDARD", retentionFloorDays: 90, retrieval: "immediate", retrievalOptions: [], provisional: false },
];

/** Every class of one provider, in the catalogue's order. */
export function storageClassesOf(provider: StorageProvider): StorageClass[] {
  return STORAGE_CLASSES.filter((c) => c.provider === provider);
}

/** What the catalogue says about a provider and request value. */
export type StorageClassLookup =
  | { kind: "class"; storageClass: StorageClass }
  /** A provider the catalogue knows, with no class by that request value. */
  | { kind: "unknown-class" }
  /** A provider with no rows: `UNRECOGNISED_PROVIDER_CLASS`, an unknown floor, and an ordinary read. */
  | { kind: "unrecognised-provider" };

function isStorageProvider(provider: string): provider is StorageProvider {
  return (STORAGE_PROVIDERS as readonly string[]).includes(provider);
}

/**
 * Find a class by a vault's recorded provider and the value sent on the wire,
 * spelled exactly as the provider spells it. `null` finds the class of a
 * provider that takes no value (Backblaze B2).
 */
export function lookupStorageClass(provider: string, requestValue: string | null): StorageClassLookup {
  if (!isStorageProvider(provider)) return { kind: "unrecognised-provider" };
  const storageClass = storageClassesOf(provider).find((c) => c.requestValue === requestValue);
  return storageClass ? { kind: "class", storageClass } : { kind: "unknown-class" };
}

/**
 * Why a new vault cannot be created on a storage tier — the store crate's
 * `CannotCreate`. `waits-on-retrieval`: a read waits on a retrieval request,
 * and the class offers no retrieval option the app can ask for; no catalogue
 * row is one today.
 * `class-header-unconfirmed`: Google Cloud Storage's other classes wait on
 * confirming its class header.
 */
export type CannotCreateReason = "waits-on-retrieval" | "class-header-unconfirmed";

/** Whether a new vault can be created on a storage tier, and why not when it cannot. */
export type CreateVaultAnswer = { ok: true } | { ok: false; reason: CannotCreateReason };

/**
 * Whether a new vault can be created on a storage tier — a copy of the store
 * crate's `can_create_repository_on`, for the desktop's picker and the portal
 * (#763). A function rather than a catalogue column: it says what the app can
 * do yet, not a fact about the provider.
 *
 * `null` is an unrecognised S3-compatible provider, whose one class,
 * `UNRECOGNISED_PROVIDER_CLASS`, has no row.
 */
export function canCreateVaultOn(storageClass: StorageClass | null): CreateVaultAnswer {
  if (storageClass === null) return { ok: true };
  const { provider, requestValue } = storageClass;
  // A provider's standard class is its first row, as the Rust's `is_standard` reads it.
  const isStandard = storageClassesOf(provider)[0]?.requestValue === requestValue;
  if (provider === "gcs" && !isStandard) {
    return { ok: false, reason: "class-header-unconfirmed" };
  }
  if (storageClass.retrieval === "delayed" && storageClass.retrievalOptions.length === 0) {
    return { ok: false, reason: "waits-on-retrieval" };
  }
  return { ok: true };
}
